import { useState } from 'react'
import s from './Details.scss'
import cn from 'classnames'
import {
  KeyValuePairs,
  InputContainer,
  GoogleAutocomplete,
  Checkbox
} from 'components/ideal-core-ui'
import Select from 'react-select'
import { fromConstantPropertyToSelectOptions } from 'utils/serializers'
import { APP_CONSTANTS } from 'utils/constants'
import { Offices } from 'components/common/Offices'
import { timezoneDate, utcDate } from 'utils/helpers'
import { getDateFormat, getCurrency } from 'utils/authService'
import DatePicker from 'react-datepicker'
import '!style-loader!css-loader!react-datepicker/dist/react-datepicker.css' // eslint-disable-line
import moment from 'moment'
import { FaAngleRight, FaAngleDown } from 'react-icons/fa'
import { NumericFormat } from 'react-number-format'

const Details = ({
  deal,
  users,
  changeProperty,
  onSelectCallback,
  categories,
  sources,
  orderedStatuses,
  workTypes,
  bidStatuses,
  internalBidStatuses,
  usingQuoteNumberAutomation,
  generateQuoteNumber
}) => {
  const [isMoreInfoVisible, setIsMoreInfoVisible] = useState(false)
  return (
    <>
      <div className={cn(s.wrapper, s.details)}>
        <div className={s.column}>
          <KeyValuePairs
            pairs={[
              {
                key: '* Opportunity Name',
                value: (
                  <InputContainer
                    type="text"
                    text={deal.name}
                    placeholder="Enter Opportunity Name"
                    cb={value => changeProperty(value, 'name')}
                    cy="deal_name_input"
                  />
                )
              },
              {
                key: '* Bid Due Date',
                valueClass: [s.dueDateWrapper],
                value: (
                  <DatePicker
                    selected={moment(
                      timezoneDate(deal.dueDate, 'HH:mm:ss'),
                      `${getDateFormat()} HH:mm:ss`
                    ).toDate()}
                    startDate={moment(
                      timezoneDate(deal.dueDate, 'HH:mm:ss'),
                      `${getDateFormat()} HH:mm:ss`
                    ).toDate()}
                    onChange={value => {
                      if (!value) return
                      changeProperty(utcDate(value), 'dueDate')
                    }}
                    showTimeSelect
                    timeFormat="h:mm aa"
                    timeIntervals={15}
                    dateFormat={`${getDateFormat()
                      .replace('YYYY', 'yyyy')
                      .replace('DD', 'dd')} h:mm aa`}
                    timeCaption="time"
                    placeholderText="Select deal due date"
                    className={s.dueDate}
                  />
                )
              },
              {
                key: '* Opportunity Stage/Status',
                value: (
                  <Select
                    onChange={value => changeProperty(value, 'dealStatus')}
                    options={orderedStatuses}
                    id="deal_stage_select_cy"
                    value={deal.dealStatus}
                  />
                )
              },
              {
                key: '* Opportunity Manager',
                value: (
                  <Select
                    onChange={value => changeProperty(value, 'dealmanagerid')}
                    options={users}
                    value={deal.dealmanagerid}
                  />
                )
              },
              {
                key: (
                  <>
                    <span>Quote Number</span>
                    <br />
                    {usingQuoteNumberAutomation && !deal.quoteNumber && (
                      <span className={s.link} onClick={generateQuoteNumber}>
                        generate quote no
                      </span>
                    )}
                  </>
                ),
                value: (
                  <InputContainer
                    type="text"
                    text={(deal.quoteNumber && String(deal.quoteNumber)) || ''}
                    placeholder="Enter Quote Number"
                    cb={value => changeProperty(value, 'quoteNumber')}
                  />
                )
              },
              {
                key: 'Description',
                value: (
                  <InputContainer
                    type="textarea"
                    text={deal.description}
                    classname={s.briefNote}
                    placeholder="Enter description"
                    cb={value => changeProperty(value, 'description')}
                  />
                )
              },
              {
                key: 'Work Type',
                value: (
                  <Select
                    onChange={value => changeProperty(value, 'workType')}
                    options={workTypes}
                  />
                )
              }
            ]}
          />
        </div>
        <div className={s.column}>
          <KeyValuePairs
            pairs={[
              {
                key: 'Address',
                value: (
                  <GoogleAutocomplete
                    value={deal.address}
                    onChange={value => changeProperty(value, 'address')}
                    onSelectCallback={onSelectCallback}
                    cy="deal_address_input"
                  />
                )
              },
              {
                key: 'Street Address',
                value: (
                  <InputContainer
                    type="text"
                    text={deal.address2}
                    placeholder="Street 1"
                    cb={value => changeProperty(value, 'address2')}
                  />
                )
              },
              {
                key: 'Street 2',
                value: (
                  <InputContainer
                    type="text"
                    text={deal.street2}
                    placeholder="Street 2"
                    cb={value => changeProperty(value, 'street2')}
                  />
                )
              },
              {
                key: 'City',
                value: (
                  <InputContainer
                    type="text"
                    text={deal.city}
                    placeholder="Enter City"
                    cb={value => changeProperty(value, 'city')}
                    cy="deal_city_input"
                  />
                )
              },
              {
                key: 'State/Province/Region',
                value: (
                  <InputContainer
                    type="text"
                    text={deal.state}
                    placeholder="Enter State/Province/Region"
                    cb={value => changeProperty(value, 'state')}
                    cy="deal_region_input"
                  />
                )
              },
              {
                key: 'Zip/Postal Code',
                value: (
                  <InputContainer
                    type="text"
                    text={deal.zipCode}
                    placeholder="Enter Zip/Postal Code"
                    cb={value => changeProperty(value, 'zipCode')}
                  />
                )
              },
              {
                key: 'Country',
                value: (
                  <InputContainer
                    type="text"
                    text={deal.country}
                    placeholder="Enter Country"
                    cb={value => changeProperty(value, 'country')}
                  />
                )
              }
            ]}
          />
          <Offices
            dealTeams={deal.teams}
            className={s.offices}
            teamsCb={teams => {
              changeProperty(teams, 'teams')
            }}
          />
        </div>
      </div>
      <p
        data-cy="show_more_btn"
        className={s.additionalInfo}
        onClick={() => setIsMoreInfoVisible(!isMoreInfoVisible)}
      >
        {isMoreInfoVisible ? <FaAngleDown /> : <FaAngleRight />} Additional Info
      </p>
      {isMoreInfoVisible && (
        <div className={cn(s.wrapper, s.details, s.additionalInfoWrapper)}>
          <div className={s.column}>
            <KeyValuePairs
              pairs={[
                {
                  key: `Opportunity Value ${getCurrency().symbol}`,
                  keyClass: [s.label],
                  valueClass: [s.value],
                  value: (
                    <NumericFormat
                      thousandSeparator
                      decimalScale={2}
                      prefix={getCurrency().symbol}
                      onValueChange={formattedValue => {
                        changeProperty(+formattedValue.value, 'value')
                      }}
                      allowNegative={false}
                    />
                  )
                },
                {
                  key: 'Opportunity Category',
                  value: (
                    <Select
                      onChange={value => changeProperty(value, 'dealCategory')}
                      options={categories}
                      id="deal_category_select_cy"
                    />
                  )
                },
                {
                  key: 'Client Bid Stage',
                  value: (
                    <Select
                      onChange={value => changeProperty(value, 'bidStatus')}
                      options={bidStatuses}
                    />
                  )
                },
                {
                  key: 'Internal Bid Status',
                  value: (
                    <Select
                      onChange={value => changeProperty(value, 'internalBidStatus')}
                      options={internalBidStatuses}
                      value={deal.internalBidStatus}
                    />
                  )
                },
                {
                  key: 'Opportunity Source',
                  value: (
                    <Select
                      onChange={value => changeProperty(value, 'dealSource')}
                      options={sources}
                    />
                  )
                },
                {
                  key: 'Opportunity Size',
                  value: (
                    <Select
                      onChange={value => changeProperty(value, 'size')}
                      options={fromConstantPropertyToSelectOptions(
                        Object.keys(APP_CONSTANTS.DealSize),
                        'DealSize'
                      )}
                      id="deal_size_select_cy"
                    />
                  )
                }
              ]}
            />
          </div>
          <div className={s.column}>
            <KeyValuePairs
              pairs={[
                {
                  key: 'Brief Note',
                  value: (
                    <InputContainer
                      type="textarea"
                      text={deal.briefNote}
                      classname={s.briefNote}
                      placeholder="Enter a brief note"
                      cb={value => changeProperty(value, 'briefNote')}
                    />
                  )
                },
                {
                  key: 'Link To Plans http(s)://www.example.com',
                  value: (
                    <InputContainer
                      type="text"
                      text={deal.linkToPlans}
                      placeholder="Enter Link To Plans"
                      cb={value => changeProperty(value, 'linkToPlans')}
                    />
                  )
                }
              ]}
            />
            <div className={cn(s.wrapper, s.bidding)}>
              <div className={s.column}>
                <KeyValuePairs
                  pairs={[
                    {
                      key: 'Hard Bid',
                      keyClass: [s.bidLabel],
                      value: (
                        <Checkbox
                          isChecked={deal.hardBid}
                          triggerCheckbox={value => changeProperty(value.target.checked, 'hardBid')}
                        />
                      )
                    }
                  ]}
                />
              </div>
              <div className={s.column}>
                <KeyValuePairs
                  pairs={[
                    {
                      key: 'Sealed Bid',
                      keyClass: [s.bidLabel],
                      value: (
                        <Checkbox
                          isChecked={deal.sealedBid}
                          triggerCheckbox={value =>
                            changeProperty(value.target.checked, 'sealedBid')
                          }
                        />
                      )
                    }
                  ]}
                />
              </div>
              <div className={s.column}>
                <KeyValuePairs
                  pairs={[
                    {
                      key: 'Prevailing Wage',
                      keyClass: [s.bidLabel],
                      value: (
                        <Checkbox
                          isChecked={deal.prevailingWage}
                          triggerCheckbox={value =>
                            changeProperty(value.target.checked, 'prevailingWage')
                          }
                        />
                      )
                    }
                  ]}
                />
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  )
}

export default Details
